import { Banner, BannerPlacement, ShowcaseFilter } from '../banners/types';
import httpClient from '../../services/httpClient';
import { ActivationType, FulfillmentType, Game, GameStore, PlatformFamily, Product, PurchaseRegion } from '../product/types';

export type { Banner } from '../banners/types';

export interface AdminUser {
  id: number;
  telegramId?: number;
  email?: string;
  firstName?: string;
  lastName?: string;
  userName?: string;
  adminRole: boolean;
}

export interface CreateGamePayload {
  name: string;
  description: string;
  price: number | null;
  picture: string;
  platforms: string[];
  genres: string[];
  dateRelease?: string | null;
  completionTime?: number | null;
  online: boolean;
  multiplayer: boolean;
  screenshots: string[];
  preOrder: boolean;
  brandNew: boolean;
  salesLeader: boolean;
  lowestPrice: boolean;
  topMetacritic: boolean;
  showcasePinned: boolean;
  listDLC: Array<{
    id?: number;
    name: string;
    description: string;
    price: number;
    picture?: string;
    purchaseOffers: CreateGamePayload['purchaseOffers'];
  }>;
  platformFamily: PlatformFamily | '';
  store: GameStore | '';
  purchaseOffers: Array<{
    id?: number;
    name: string;
    description: string;
    price: number;
    region?: PurchaseRegion | null;
    fulfillmentType: FulfillmentType;
    activationType: ActivationType;
    isAvailable: boolean;
  }>;
}

export interface MediaBackfillFailure {
  id: number;
  objectKey: string;
  error: string;
}

export interface MediaBackfillStatus {
  state: 'idle' | 'queued' | 'running' | 'completed' | 'completed_with_errors' | 'failed' | 'cancelled';
  total: number;
  processed: number;
  succeeded: number;
  failed: number;
  remaining: number;
  startedAt?: string | null;
  finishedAt?: string | null;
  failures: MediaBackfillFailure[];
}

// Категории онлайн-сервисов — value совпадает с backend ServiceCategory
export const SERVICE_CATEGORIES: { value: string; label: string }[] = [
  { value: 'Subscription', label: 'Подписка' },
  { value: 'GameService', label: 'Игровой сервис' },
  { value: 'Entertainment', label: 'Развлечения' },
  { value: 'AI', label: 'AI-сервисы' },
  { value: 'Work', label: 'Для работы' },
  { value: 'Other', label: 'Другое' },
];

export interface ServicePlanPayload {
  id?: number; // существующий тариф — для diff при обновлении; отсутствует у новых
  name: string;
  description: string;
  price: number;
  tier?: string | null;
  durationMonths?: number | null;
}

export interface OnlineServicePayload {
  name: string;
  description: string;
  price: number;
  picture: string;
  category: string;
  platforms: string[];
  screenshots: string[];
  brandNew: boolean;
  salesLeader: boolean;
  plans: ServicePlanPayload[];
}

export interface ServicePlan extends Product {
  tier?: string | null;
  durationMonths?: number | null;
}

export interface OnlineService extends Product {
  category: string;
  platforms: string[];
  screenshots: string[];
  brandNew: boolean;
  salesLeader: boolean;
  plans: ServicePlan[];
}

export interface Category {
  id: number;
  name: string;
  slug: string;
  position: number;
  description?: string | null;
  serviceCategory?: string | null;
}

export interface CategoryUpsert {
  name: string;
  slug: string;
  description?: string | null;
  serviceCategory?: string | null;
}

export interface BannerUpsert {
  placement?: BannerPlacement;
  showcaseFilter?: ShowcaseFilter | null;
  categoryId?: number | null;
  externalUrl?: string | null;
  isSharing?: boolean;
}

export interface PlatformCard {
  id: number;
  name: string;
  platforms: string[];
  imageUrl?: string | null;
  isSharing: boolean;
  position: number;
}

export interface PlatformCardUpsert {
  name: string;
  platforms: string[];
  isSharing?: boolean;
}

export const adminService = {
  fetchUsers: async (): Promise<AdminUser[]> => {
    const { data } = await httpClient.get('/api/Admin/Users');
    return Array.isArray(data) ? data : [];
  },

  createGame: async (payload: CreateGamePayload): Promise<Game> => {
    const { data } = await httpClient.post('/api/Admin/CreateGame', payload);
    return data as Game;
  },

  updateGame: async (id: number, payload: CreateGamePayload): Promise<Game> => {
    const { data } = await httpClient.put('/api/Admin/UpdateGame', payload, {
      params: { id },
    });
    return data as Game;
  },

  deleteGame: async (id: number): Promise<void> => {
    await httpClient.delete('/api/Admin/DeleteGame', {
      params: { id },
    });
  },

  fetchOnlineServices: async (params?: {
    name?: string;
    category?: string;
    pageNumber?: number;
    pageSize?: number;
  }): Promise<{ products: OnlineService[]; total: number }> => {
    const { data } = await httpClient.get('/api/OnlineService', { params });
    const products = Array.isArray(data?.products) ? data.products : [];
    return { products, total: typeof data?.total === 'number' ? data.total : products.length };
  },

  fetchOnlineService: async (id: number): Promise<OnlineService> => {
    const { data } = await httpClient.get(`/api/OnlineService/${id}`);
    const service = (data?.product ?? data) as any;
    // Разворачиваем возможный $values у коллекции тарифов (ReferenceHandler)
    if (service?.plans?.$values && Array.isArray(service.plans.$values)) {
      service.plans = service.plans.$values;
    }
    return service as OnlineService;
  },

  createOnlineService: async (payload: OnlineServicePayload): Promise<OnlineService> => {
    const { data } = await httpClient.post('/api/Admin/CreateOnlineService', payload);
    return data as OnlineService;
  },

  updateOnlineService: async (id: number, payload: OnlineServicePayload): Promise<OnlineService> => {
    const { data } = await httpClient.put('/api/Admin/UpdateOnlineService', payload, {
      params: { id },
    });
    return data as OnlineService;
  },

  deleteOnlineService: async (id: number): Promise<void> => {
    await httpClient.delete('/api/Admin/DeleteOnlineService', {
      params: { id },
    });
  },

  // Картинки хранятся в таблице ProductImage и грузятся к УЖЕ существующему товару
  // (нужен его id). Поэтому в формах создания сначала создаём товар, затем грузим картинки.
  uploadMasterImage: async (productId: number, file: File): Promise<string> => {
    const form = new FormData();
    form.append('file', file);
    const { data } = await httpClient.post(`/api/Images/master/${productId}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data?.url ?? '';
  },

  uploadScreenshotImage: async (
    productId: number,
    file: File
  ): Promise<{ id: number; url: string }> => {
    const form = new FormData();
    form.append('file', file);
    const { data } = await httpClient.post(`/api/Images/screenshot/${productId}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { id: data?.id ?? 0, url: data?.url ?? '' };
  },

  deleteScreenshotImage: async (imageId: number): Promise<void> => {
    await httpClient.delete(`/api/Images/screenshot/${imageId}`);
  },

  startMediaBackfill: async (): Promise<MediaBackfillStatus> => {
    const { data } = await httpClient.post('/api/Images/backfill/start');
    return data as MediaBackfillStatus;
  },

  getMediaBackfillStatus: async (): Promise<MediaBackfillStatus> => {
    const { data } = await httpClient.get('/api/Images/backfill/status');
    return data as MediaBackfillStatus;
  },

  // ─── Категории (управляемый список для навигации/баннеров) ───────────────────
  fetchCategories: async (): Promise<Category[]> => {
    const { data } = await httpClient.get('/api/Categories');
    return Array.isArray(data) ? data : [];
  },

  createCategory: async (payload: CategoryUpsert): Promise<Category> => {
    const { data } = await httpClient.post('/api/Categories', payload);
    return data as Category;
  },

  updateCategory: async (id: number, payload: CategoryUpsert): Promise<Category> => {
    const { data } = await httpClient.put(`/api/Categories/${id}`, payload);
    return data as Category;
  },

  deleteCategory: async (id: number): Promise<void> => {
    await httpClient.delete(`/api/Categories/${id}`);
  },

  reorderCategories: async (orderedIds: number[]): Promise<void> => {
    await httpClient.put('/api/Categories/reorder', orderedIds);
  },

  // ─── Баннеры карусели на главной ────────────────────────────────────────────
  fetchBanners: async (placement: BannerPlacement = 'home'): Promise<Banner[]> => {
    const { data } = await httpClient.get('/api/Banners', { params: { placement } });
    return Array.isArray(data) ? data : [];
  },

  createBanner: async (payload: BannerUpsert): Promise<Banner> => {
    const { data } = await httpClient.post('/api/Banners', payload);
    return data as Banner;
  },

  updateBanner: async (id: number, payload: BannerUpsert): Promise<Banner> => {
    const { data } = await httpClient.put(`/api/Banners/${id}`, payload);
    return data as Banner;
  },

  deleteBanner: async (id: number): Promise<void> => {
    await httpClient.delete(`/api/Banners/${id}`);
  },

  reorderBanners: async (orderedIds: number[], placement: BannerPlacement = 'home'): Promise<void> => {
    await httpClient.put('/api/Banners/reorder', orderedIds, { params: { placement } });
  },

  uploadBannerImage: async (id: number, file: File): Promise<Banner> => {
    const form = new FormData();
    form.append('file', file);
    const { data } = await httpClient.post(`/api/Banners/${id}/image`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data as Banner;
  },

  // ─── Карточки игровых платформ (карусель на главной) ─────────────────────────
  fetchPlatformCards: async (): Promise<PlatformCard[]> => {
    const { data } = await httpClient.get('/api/PlatformCards');
    return Array.isArray(data) ? data : [];
  },

  createPlatformCard: async (payload: PlatformCardUpsert): Promise<PlatformCard> => {
    const { data } = await httpClient.post('/api/PlatformCards', payload);
    return data as PlatformCard;
  },

  updatePlatformCard: async (id: number, payload: PlatformCardUpsert): Promise<PlatformCard> => {
    const { data } = await httpClient.put(`/api/PlatformCards/${id}`, payload);
    return data as PlatformCard;
  },

  deletePlatformCard: async (id: number): Promise<void> => {
    await httpClient.delete(`/api/PlatformCards/${id}`);
  },

  reorderPlatformCards: async (orderedIds: number[]): Promise<void> => {
    await httpClient.put('/api/PlatformCards/reorder', orderedIds);
  },

  uploadPlatformCardImage: async (id: number, file: File): Promise<PlatformCard> => {
    const form = new FormData();
    form.append('file', file);
    const { data } = await httpClient.post(`/api/PlatformCards/${id}/image`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data as PlatformCard;
  },
};
